import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * ReportAudiogram — non-interactive audiogram for the printable report.
 *
 * 2026-09-01 (Neo follow-up): the SVG now DERIVES its viewBox from the
 * actual pixel size of the wrapping div, so the chart fills 100 % of
 * BOTH width and height of its container — no more ~60 px dead
 * whitespace above/below the plot when the sidebar layout gives it a
 * 4:3-or-taller box (e.g. `size = large` at 420 px height).
 *
 * How it works
 * ------------
 *  1. A tiny useLayoutEffect + ResizeObserver measures the wrapping div.
 *  2. viewBox === measured pixels (e.g. `0 0 347 420`), so 1 SVG unit
 *     always maps to 1 CSS pixel. Circles / triangles / text keep their
 *     intrinsic on-screen size regardless of container aspect ratio.
 *  3. The plot area (chart_w × chart_h) is (measured - PAD), so grid
 *     spacing scales with the container — a tall box stretches the
 *     dB rows, a wide box stretches the frequency columns. Symbols
 *     stay round because their radius is expressed in the same
 *     pixel-equivalent units as the viewBox.
 *  4. On first paint (before the effect fires) we render at the fallback
 *     600 × 400 so the chart is never empty even for SSR-style paints.
 *
 * ReportAudiogram interface is unchanged so PureToneSection + Report 2
 * continue to work: pass `rightEarData` / `leftEarData` / `title`.
 */

const FREQUENCIES = [125, 250, 500, 750, 1000, 1500, 2000, 3000, 4000, 6000, 8000];
const MAJOR_FREQS = [125, 250, 500, 1000, 2000, 4000, 8000];
const DB_LEVELS = Array.from({ length: 27 }, (_, i) => -10 + i * 5);
const MAJOR_DB_LEVELS = Array.from({ length: 14 }, (_, i) => -10 + i * 10);

const EAR_COLOR = { right: '#DC3545', left: '#007BFF' };

// Fallback viewport used on the very first render before the layout
// effect measures the wrapping div. The runtime measurement replaces
// this within the same tick so users never see the fallback aspect.
const FALLBACK = { w: 600, h: 400 };
const PAD = { top: 24, right: 16, bottom: 32, left: 40 };

// --- Symbol helpers (kept identical to the previous fixed-viewBox
//     version — visual output matches 1:1) ------------------------
const RIGHT_R = 6;
const SQ = 5;

const RightAcSymbol = ({ x, y, masked, color }) =>
  masked ? (
    <polygon
      points={`${x},${y - RIGHT_R} ${x + RIGHT_R},${y + RIGHT_R} ${x - RIGHT_R},${y + RIGHT_R}`}
      fill={color}
      stroke={color}
      strokeWidth="1.5"
    />
  ) : (
    <circle cx={x} cy={y} r={RIGHT_R} fill="none" stroke={color} strokeWidth="2" />
  );

const LeftAcSymbol = ({ x, y, masked, color }) =>
  masked ? (
    <rect
      x={x - SQ} y={y - SQ} width={SQ * 2} height={SQ * 2}
      fill={color} stroke={color} strokeWidth="1.5"
    />
  ) : (
    <g stroke={color} strokeWidth="2" fill="none">
      <line x1={x - RIGHT_R} y1={y - RIGHT_R} x2={x + RIGHT_R} y2={y + RIGHT_R} />
      <line x1={x + RIGHT_R} y1={y - RIGHT_R} x2={x - RIGHT_R} y2={y + RIGHT_R} />
    </g>
  );

const BcSymbol = ({ x, y, ear, masked, color }) => {
  const sym = masked
    ? (ear === 'right' ? '[' : ']')
    : (ear === 'right' ? '<' : '>');
  return (
    <text
      x={x} y={y}
      fill={color}
      fontFamily="Arial, sans-serif"
      fontWeight="700"
      fontSize="13"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {sym}
    </text>
  );
};

const NRArrow = ({ x, y, ear, color }) => {
  const diag = Math.SQRT1_2;
  const offset = 6, shaft = 12, head = 5;
  const dx = ear === 'right' ? -diag : diag;
  const dy = diag;
  const sx = x + offset * dx;
  const sy = y + offset * dy;
  const ex = sx + shaft * dx;
  const ey = sy + shaft * dy;
  const ang = Math.atan2(dy, dx);
  const sp = Math.PI / 6;
  return (
    <g stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round">
      <line x1={sx} y1={sy} x2={ex} y2={ey} />
      <line x1={ex} y1={ey}
            x2={ex - head * Math.cos(ang - sp)}
            y2={ey - head * Math.sin(ang - sp)} />
      <line x1={ex} y1={ey}
            x2={ex - head * Math.cos(ang + sp)}
            y2={ey - head * Math.sin(ang + sp)} />
    </g>
  );
};

// Build an SVG path string that lifts the pen on every NR point so the
// polyline never crosses an "×" arrow.
const buildLinePath = (points) => {
  let d = '';
  let penUp = true;
  points.forEach((p) => {
    if (p.no_response) { penUp = true; return; }
    d += penUp ? `M ${p.x} ${p.y} ` : `L ${p.x} ${p.y} `;
    penUp = false;
  });
  return d.trim();
};

const Ear = ({ data, ear, logX, yFor }) => {
  if (!data) return null;
  const color = EAR_COLOR[ear];

  const acceptedPoints = (measurements) =>
    (measurements || [])
      .filter((m) => m && m.threshold_db !== null && m.threshold_db !== undefined
                          && FREQUENCIES.includes(m.frequency))
      .map((m) => ({
        x: logX(m.frequency),
        y: yFor(m.threshold_db),
        masked: !!m.masked,
        no_response: !!m.no_response,
        frequency: m.frequency,
      }))
      .filter((p) => p.y !== null)
      .sort((a, b) => a.frequency - b.frequency);

  const acPoints = acceptedPoints(data.ac_measurements);
  const bcPoints = acceptedPoints(data.bc_measurements);
  const acPath = buildLinePath(acPoints);
  const bcPath = buildLinePath(bcPoints);
  return (
    <g data-testid={`report-audiogram-ear-${ear}`}>
      {acPath && (
        <path d={acPath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      )}
      {bcPath && (
        <path d={bcPath} fill="none" stroke={color} strokeWidth="1.8" strokeDasharray="4 3" strokeLinejoin="round" />
      )}
      {acPoints.map((p) => (
        <g key={`ac-${p.frequency}`}>
          {ear === 'right'
            ? <RightAcSymbol x={p.x} y={p.y} masked={p.masked} color={color} />
            : <LeftAcSymbol  x={p.x} y={p.y} masked={p.masked} color={color} />
          }
          {p.no_response && <NRArrow x={p.x} y={p.y} ear={ear} color={color} />}
        </g>
      ))}
      {bcPoints.map((p) => (
        <g key={`bc-${p.frequency}`}>
          <BcSymbol x={p.x} y={p.y} ear={ear} masked={p.masked} color={color} />
          {p.no_response && <NRArrow x={p.x} y={p.y} ear={ear} color={color} />}
        </g>
      ))}
    </g>
  );
};

const ReportAudiogram = ({ rightEarData, leftEarData, title }) => {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState(FALLBACK);

  // Measure the wrapping div at layout time and subscribe to resizes so
  // the chart always matches its container's actual pixel dims — kills
  // the ~60 px whitespace-above-and-below issue at the `large`/`xlarge`
  // sizes without introducing aspect-ratio distortion.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const nextW = Math.round(r.width);
      const nextH = Math.round(r.height);
      setDims((prev) =>
        Math.abs(prev.w - nextW) > 0.5 || Math.abs(prev.h - nextH) > 0.5
          ? { w: nextW, h: nextH }
          : prev,
      );
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    return undefined;
  }, []);

  const VB = dims;
  const CHART_W = Math.max(0, VB.w - PAD.left - PAD.right);
  const CHART_H = Math.max(0, VB.h - PAD.top - PAD.bottom);

  const logX = (freq) => {
    const minF = FREQUENCIES[0];
    const maxF = FREQUENCIES[FREQUENCIES.length - 1];
    const r = (Math.log10(freq) - Math.log10(minF)) / (Math.log10(maxF) - Math.log10(minF));
    return PAD.left + r * CHART_W;
  };

  const yFor = (db) => {
    const idx = DB_LEVELS.indexOf(db);
    if (idx < 0) return null;
    return PAD.top + (idx / (DB_LEVELS.length - 1)) * CHART_H;
  };

  return (
    <div ref={wrapRef} className="w-full h-full" data-testid="report-audiogram-wrap">
      <svg
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        // `preserveAspectRatio="none"` — safe because our viewBox now
        // tracks container pixels 1:1, so nothing actually stretches;
        // this just makes intent unambiguous ("SVG fills its box").
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        className="border border-gray-400 bg-white"
        shapeRendering="geometricPrecision"
        style={{ imageRendering: 'crisp-edges', display: 'block' }}
        data-testid="report-audiogram-svg"
        role="img"
        aria-label={title ? `${title} audiogram` : 'Audiogram'}
      >
        {/* Optional per-chart title (split-mode Left / Right label). */}
        {title && (
          <text
            x={PAD.left + CHART_W / 2}
            y={14}
            fill="#1f2937"
            fontFamily="Arial, sans-serif"
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
          >
            {title}
          </text>
        )}

        {/* Horizontal grid + dB labels */}
        {DB_LEVELS.map((db) => {
          const y = yFor(db);
          const major = MAJOR_DB_LEVELS.includes(db);
          return (
            <g key={`db-${db}`}>
              <line
                x1={PAD.left} y1={y}
                x2={PAD.left + CHART_W} y2={y}
                stroke={major ? '#c0c0c0' : '#ececec'}
                strokeWidth={major ? 0.6 : 0.3}
              />
              {major && (
                <text
                  x={PAD.left - 6} y={y + 3}
                  fill="#444"
                  fontFamily="Arial, sans-serif"
                  fontSize="9"
                  textAnchor="end"
                >
                  {db}
                </text>
              )}
            </g>
          );
        })}

        {/* Vertical grid + frequency labels */}
        {FREQUENCIES.map((f) => {
          const x = logX(f);
          const major = MAJOR_FREQS.includes(f);
          const label = f >= 1000 ? `${f / 1000}K` : `${f}`;
          return (
            <g key={`f-${f}`}>
              <line
                x1={x} y1={PAD.top}
                x2={x} y2={PAD.top + CHART_H}
                stroke={major ? '#c0c0c0' : '#ececec'}
                strokeWidth={major ? 0.6 : 0.3}
                strokeDasharray={major ? undefined : '2 2'}
              />
              {major && (
                <text
                  x={x} y={VB.h - 12}
                  fill="#444"
                  fontFamily="Arial, sans-serif"
                  fontSize="9"
                  textAnchor="middle"
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}

        {/* Chart border */}
        <rect
          x={PAD.left} y={PAD.top}
          width={CHART_W} height={CHART_H}
          fill="none" stroke="#666" strokeWidth="1"
        />

        {/* Axis titles */}
        <text
          x={PAD.left + CHART_W / 2} y={VB.h - 2}
          fill="#333"
          fontFamily="Arial, sans-serif"
          fontSize="9"
          fontWeight="700"
          textAnchor="middle"
        >
          Frequency (Hz)
        </text>
        <text
          x={10} y={PAD.top + CHART_H / 2}
          fill="#333"
          fontFamily="Arial, sans-serif"
          fontSize="9"
          fontWeight="700"
          textAnchor="middle"
          transform={`rotate(-90 10 ${PAD.top + CHART_H / 2})`}
        >
          Hearing Level (dB HL)
        </text>

        {/* Ears — right first, left on top so any overlapping-at-same-freq
            markers still show the left-ear glyph, matching the interactive canvas. */}
        <Ear data={rightEarData} ear="right" logX={logX} yFor={yFor} />
        <Ear data={leftEarData}  ear="left"  logX={logX} yFor={yFor} />
      </svg>
    </div>
  );
};

export default ReportAudiogram;
